<script setup lang="ts">
import { ref } from 'vue';
import { CircleCheck, CircleAlert, X, Check, LoaderCircle } from 'lucide-vue-next';
import { inputVariants } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const props = defineProps<{
    baseUrl: string;
    name?: string;
    defaultValue?: string;
    entryId?: string | number;
    placeholder?: string;
}>();

const slug = ref(props.defaultValue ?? '');
const status = ref<'idle' | 'checking' | 'available' | 'taken'>('idle');
let timer: ReturnType<typeof setTimeout>;

function slugify(text: string, trim = true): string {
    let result = text
        .replace(/^https?:\/\//i, '')
        .replace(/^www\./i, '')
        .replace(/\/.*/g, '')
        .toLowerCase()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .replace(/[^a-z0-9-]+/g, '-')
        .replace(/-{2,}/g, '-');

    if (trim) {
        result = result.replace(/^-|-$/g, '');
    }

    return result;
}

async function checkSlug(value: string) {
    if (!value || value.length < 2) {
        status.value = 'idle';
        return;
    }

    status.value = 'checking';

    const params = new URLSearchParams({ slug: value });
    if (props.entryId) params.set('entryId', String(props.entryId));

    try {
        const res = await fetch(`/actions/microsites-module/micro-sites/check-slug?${params}`);
        const data = await res.json();
        status.value = data.available ? 'available' : 'taken';
    } catch {
        status.value = 'idle';
    }
}

function onInput(e: Event) {
    const input = e.target as HTMLInputElement;
    // Don't trim trailing dash while typing so user can type "my-"
    slug.value = slugify(input.value, false);
    input.value = slug.value;
    clearTimeout(timer);
    timer = setTimeout(() => {
        slug.value = slugify(slug.value);
        input.value = slug.value;
        checkSlug(slug.value);
    }, 1000);
}
</script>

<template>
    <div>
        <div class="relative flex">
            <span class="inline-flex items-center bg-muted text-muted-foreground text-sm px-3 border border-r-0 rounded-l-md whitespace-nowrap">
                {{ baseUrl }}
            </span>
            <input
                type="text"
                :name="name ?? 'slug'"
                :value="slug"
                :placeholder="placeholder ?? 'mein-unternehmen'"
                :class="cn(
                    inputVariants(),
                    'w-full rounded-l-none pr-9',
                    status === 'available' && '!border-green-500 !focus-visible:ring-green-500/30',
                    status === 'taken' && '!border-destructive !focus-visible:ring-destructive/30',
                )"
                @input="onInput"
            >
            <span class="absolute inset-y-0 right-0 flex items-center pr-2.5 pointer-events-none">
                <LoaderCircle v-if="status === 'checking'" class="w-4 h-4 text-muted-foreground animate-spin" />
                <CircleCheck v-else-if="status === 'available'" class="w-4 h-4 text-green-500" />
                <CircleAlert v-else-if="status === 'taken'" class="w-4 h-4 text-destructive" />
            </span>
        </div>

        <div v-if="slug" class="mt-2 rounded-md px-3 py-2 text-sm" :class="status === 'taken' ? 'bg-red-50 text-destructive' : 'bg-blue-50 text-blue-800'">
            <template v-if="status === 'taken'">
                <CircleAlert class="w-4 h-4 inline -mt-0.5 mr-1" />
                Diese URL ist bereits vergeben. Bitte wählen Sie eine andere.
            </template>
            <template v-else>
                <CircleCheck v-if="status === 'available'" class="w-4 h-4 inline -mt-0.5 mr-1 text-green-600" />
                Ihre Seite wird unter <strong>{{ baseUrl }}{{ slug }}</strong> erreichbar sein.
            </template>
        </div>

        <div v-else class="mt-1.5">
            <slot />
        </div>
    </div>
</template>
